import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Rate, TopRatesFilter } from '../states/dashboard-state';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  createReadOfficeIdsResponse,
  createReadRatesResponse,
  createReadShortTopRatesRequest,
  createReadStatistcsRequest,
  createReadStatisticsResponse,
  createReadTopRatesRequest,
  createReadTopRatesResponse,
  createShortTopRatesUrlParams,
  createStatisticsUrlParams,
  createTopRatesUrlParams,
  toBookingOfficesResponse,
  toRates,
  toStatisticsResponse,
  toTopRatesResponse
} from './backend-dashboard-converter';
import { StatisticsFilter } from '../../../../../redux/models/statistics/statistics-filter';
import * as moment from 'moment';
import {
  BookingOfficesResponse,
  ReadOfficeIdsResult,
  ReadStatistcsResponse,
  ReadStatistcsResult,
  ReadTopRatesResponse,
  ReadTopRatesResult,
  TopRate
} from './dashboard.model';
import Cookies from 'js-cookie';
import { JWT_TYPE_NAME, ReefHeaders, ReefTokenService } from '@seco/core';
import { isOn } from 'feature-toggle-service';
import { ICompanyNameAndOffice } from '../../models/dashboard.model';

/** old amadeus-dashboard server endpoints */
const avcStatisticsUrl = '/db/dashboard/v1/booking/statistics';
const avcTopRatesUrl = '/db/dashboard/v1/booking/toprates';
const avcOfficesUrl = '/db/dashboard/v1/booking/bookingoffices';

/** new avc-admin server endpoints */
const avcAdminStatisticsUrl = '/aa/api/v1/dashboard/statistics';
const avcAdminTopRatesUrl = '/aa/api/v1/dashboard/toprates';
const avcAdminOfficesUrl = '/aa/api/v1/dashboard/bookingoffices';


const avcAdminStatisticsUrlV2 = '/aa/api/v2/dashboard/statistics';
const avcAdminTopRatesUrlV2 = '/aa/api/v2/dashboard/toprates';
const avcAdminDashboardHierarchyUrlV2 = '/aa/api/v2/dashboard/hierarchy';
@Injectable({ providedIn: 'root' })
export class BackendDashboardService {
  readonly DATE_FORMAT = 'YYYY-MM-DD';

  /** is new implementation with avc-admin server endpoints enabled */
  improvedDashboardsEnabled = isOn('AD_AVC_BOOKING_IMPROVEMENT_A');
  avcNewDashboardToggleEnabled = isOn('AD_AVC_DASHBOARD_NEW_HIERARCHY');
  constructor(private readonly http: HttpClient,
    private readonly reeftokenservice: ReefTokenService
  ) { }

  readStatistcs(filter: StatisticsFilter): Observable<ReadStatistcsResponse> {
    if (this.improvedDashboardsEnabled && this.avcNewDashboardToggleEnabled) {
      const urlParams = createStatisticsUrlParams(filter, true);
      return this.http
        .get(`${this.avcUrl(avcAdminStatisticsUrlV2)}?${urlParams}`, this.avcHeaders())
        .pipe(map((response: ReadStatistcsResult) => toStatisticsResponse(response)));
    }
    if (this.improvedDashboardsEnabled) {
      const urlParams = createStatisticsUrlParams(filter, true);
      return this.http
        .get(`${this.avcUrl(avcAdminStatisticsUrl)}?${urlParams}`, this.avcHeaders())
        .pipe(map((response: ReadStatistcsResult) => toStatisticsResponse(response)));
    }
    else {
      const request = createReadStatistcsRequest(filter, true);
      return this.http
        .post(this.avcUrl(avcStatisticsUrl), request, this.avcHeaders())
        .pipe(map((response: { body: ReadStatistcsResult }) => createReadStatisticsResponse(response.body)));
    }

  }

  readOneYearAgoStatistcs(filter: StatisticsFilter): Observable<ReadStatistcsResponse> {
    const newFilter: StatisticsFilter = {
      ...filter,
      startDate: this.getDateYearOffset(filter.startDate, -1),
      endDate: this.getDateYearOffset(filter.endDate, -1),
      comparePeriodYearOffset: 1
    };

    if (this.improvedDashboardsEnabled && this.avcNewDashboardToggleEnabled) {
      const urlParams = createStatisticsUrlParams(newFilter, true);
      return this.http
        .get(`${this.avcUrl(avcAdminStatisticsUrlV2)}?${urlParams}`, this.avcHeaders())
        .pipe(map((response: ReadStatistcsResult) => toStatisticsResponse(response)));
    }
    if (this.improvedDashboardsEnabled) {
      const urlParams = createStatisticsUrlParams(newFilter, true);
      return this.http
        .get(`${this.avcUrl(avcAdminStatisticsUrl)}?${urlParams}`, this.avcHeaders())
        .pipe(map((response: ReadStatistcsResult) => toStatisticsResponse(response)));
    }
    else {
      const request = createReadStatistcsRequest(newFilter, true);
      return this.http
        .post(this.avcUrl(avcStatisticsUrl), request, this.avcHeaders())
        .pipe(map((response: { body: ReadStatistcsResult }) => createReadStatisticsResponse(response.body)));
    }
  }

  readTwoYearsAgoStatistcs(filter: StatisticsFilter): Observable<ReadStatistcsResponse> {
    const newFilter: StatisticsFilter = {
      ...filter,
      startDate: this.getDateYearOffset(filter.startDate, -2),
      endDate: this.getDateYearOffset(filter.endDate, -2),
      comparePeriodYearOffset: 2
    };
    if (this.improvedDashboardsEnabled && this.avcNewDashboardToggleEnabled) {
      const urlParams = createStatisticsUrlParams(newFilter, true);
      return this.http
        .get(`${this.avcUrl(avcAdminStatisticsUrlV2)}?${urlParams}`, this.avcHeaders())
        .pipe(map((response: ReadStatistcsResult) => toStatisticsResponse(response)));
    }
    if (this.improvedDashboardsEnabled) {
      const urlParams = createStatisticsUrlParams(newFilter, true);
      return this.http
        .get(`${this.avcUrl(avcAdminStatisticsUrl)}?${urlParams}`, this.avcHeaders())
        .pipe(map((response: ReadStatistcsResult) => toStatisticsResponse(response)));
    }
    else {
      const request = createReadStatistcsRequest(newFilter, true);
      return this.http
        .post(this.avcUrl(avcStatisticsUrl), request, this.avcHeaders())
        .pipe(map((response: { body: ReadStatistcsResult }) => createReadStatisticsResponse(response.body)));
    }
  }

  readTopRates(filter: StatisticsFilter): Observable<ReadTopRatesResponse> {
    if (this.improvedDashboardsEnabled && this.avcNewDashboardToggleEnabled) {
      const urlParams = createTopRatesUrlParams(filter);
      return this.http
        .get(`${this.avcUrl(avcAdminTopRatesUrlV2)}?${urlParams}`, this.avcHeaders())
        .pipe(map((response: TopRate[]) => toTopRatesResponse(response)));
    }
    if (this.improvedDashboardsEnabled) {
      const urlParams = createTopRatesUrlParams(filter);
      return this.http
        .get(`${this.avcUrl(avcAdminTopRatesUrl)}?${urlParams}`, this.avcHeaders())
        .pipe(map((response: TopRate[]) => toTopRatesResponse(response)));
    }
    const request = createReadTopRatesRequest(filter);
    return this.http
      .post(this.avcUrl(avcTopRatesUrl), request, this.avcHeaders())
      .pipe(map((response: { body: ReadTopRatesResult }) => createReadTopRatesResponse(response.body)));
  }

  readTopRatesHotel(filter: StatisticsFilter, topRatesFilter: TopRatesFilter, city: string): Observable<Rate[]> {
    if (this.improvedDashboardsEnabled && this.avcNewDashboardToggleEnabled) {
      const urlParams = createShortTopRatesUrlParams(filter, topRatesFilter, city, null);
      return this.http
        .get(`${this.avcUrl(avcAdminTopRatesUrlV2)}?${urlParams}`, this.avcHeaders())
        .pipe(map((response: TopRate[]) => toRates(response)));
    }
    if (this.improvedDashboardsEnabled) {
      const urlParams = createShortTopRatesUrlParams(filter, topRatesFilter, city, null);
      return this.http
        .get(`${this.avcUrl(avcAdminTopRatesUrl)}?${urlParams}`, this.avcHeaders())
        .pipe(map((response: TopRate[]) => toRates(response)));
    }
    else {
      const request = createReadShortTopRatesRequest(filter, topRatesFilter, city, null);
      return this.http
        .post(this.avcUrl(avcTopRatesUrl), request, this.avcHeaders())
        .pipe(map((response: { body: ReadTopRatesResult }) => createReadRatesResponse(response.body)));
    }
  }

  readTopRatesCity(filter: StatisticsFilter, topRatesFilter: TopRatesFilter, country: string): Observable<Rate[]> {
    if (this.improvedDashboardsEnabled && this.avcNewDashboardToggleEnabled) {
      const urlParams = createShortTopRatesUrlParams(filter, topRatesFilter, null, country);
      return this.http
        .get(`${this.avcUrl(avcAdminTopRatesUrlV2)}?${urlParams}`, this.avcHeaders())
        .pipe(map((response: TopRate[]) => toRates(response)));
    }
    if (this.improvedDashboardsEnabled) {
      const urlParams = createShortTopRatesUrlParams(filter, topRatesFilter, null, country);
      return this.http
        .get(`${this.avcUrl(avcAdminTopRatesUrl)}?${urlParams}`, this.avcHeaders())
        .pipe(map((response: TopRate[]) => toRates(response)));
    }
    else {
      const request = createReadShortTopRatesRequest(filter, topRatesFilter, null, country);
      return this.http
        .post(this.avcUrl(avcTopRatesUrl), request, this.avcHeaders())
        .pipe(map((response: { body: ReadTopRatesResult }) => createReadRatesResponse(response.body)));
    }
  }

  getBookingOffices(): Observable<BookingOfficesResponse> {
    if (this.improvedDashboardsEnabled && this.avcNewDashboardToggleEnabled) {
      return this.http
        .get(this.avcUrl(avcAdminDashboardHierarchyUrlV2), this.avcHeaders())
        .pipe(map((response: ICompanyNameAndOffice[]) => createReadOfficeIdsResponse(response)));
    }

    if (this.improvedDashboardsEnabled) {
      return this.http
        .get(`${this.avcUrl(avcAdminOfficesUrl)}`, this.avcHeaders())
        .pipe(map((response: string[]) => toBookingOfficesResponse(response)));
    }

    return this.http
      .post(this.avcUrl(avcOfficesUrl), {}, this.avcHeaders())
      .pipe(map((response: { body: ReadOfficeIdsResult }) => createReadOfficeIdsResponse(response.body)));

  }

  avcUrl(url: string): string {
    const sessionId = localStorage.getItem('JsessionId');
    return `${url};jsessionid=${sessionId}`;
  }

  avcHeaders(): {
    headers?:
    | HttpHeaders
    | {
      [header: string]: string | string[];
    };
    context?: HttpContext;
    observe?: 'body';
    params?:
    | HttpParams
    | {
      [param: string]: string | number | boolean;
    };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
  } {
    const options: any = { withCredentials: true };
    const jwtMap = this.reeftokenservice.getTokens();
    const jwt = jwtMap?.get(ReefHeaders.AUTH)?.get(JWT_TYPE_NAME);
      const headers = new HttpHeaders({
        Authorization: `Bearer ${jwt}`
      });
      options.headers = headers;
    return options;
  }

  private getDateYearOffset(dateValue: string, offset): string {
    return moment(dateValue)
      .add(offset, 'years')
      .format(this.DATE_FORMAT);
  }
}
